/*
 * @providesModule EmailOTPUtils
 */

import NetClient from 'NetClient';
import {ErrorObjectType, ErrorResponse} from 'NetClientTypes';
import {NativeModules, Platform} from 'react-native';
import {getUserDetails} from 'TelemetryUtils';
import APIUtilities from 'APIUtilities';
import SkillzModule from 'SkillzModule';

const SKZAPIManager = NativeModules.SKZAPIManager;

export interface SendEmailOTPParams {
  email: string;
  isSignUp?: boolean;
  displayName?: string;
  isBonusAvailable?: boolean;
}

export interface VerifyEmailOTPParams {
  email: string;
  token: string;
  isSignUp?: boolean;
  provide_email_verification_bonus?: boolean;
}

export interface EmailOTPResponse {
  success: boolean;
  message?: string;
  data?: object;
  code?: number; // HTTP status code
  switchedToSignIn?: boolean; // True if 409 caused auto-switch from sign-up to sign-in
}

/**
 * Sends an email OTP to the specified email address
 * @param params - Configuration object containing email, isSignUp flag, and optional display name
 * @returns Promise<EmailOTPResponse> - Response indicating success/failure and optional message
 */
export const sendEmailOTP = async ({
  email,
  isSignUp,
  displayName,
  isBonusAvailable = false,
}: SendEmailOTPParams): Promise<EmailOTPResponse> => {
  return new Promise((resolve) => {
    const url = '/user/v1/email-verification-request';
    const requestParams = {
      email: email,
      sign_up: isSignUp,
      display_name: displayName,
    };

    NetClient.postURL(
      url,
      requestParams,
      SKZAPIManager.OnError.failSilently,
      (error: ErrorResponse | null, response?: unknown) => {
        try {
          if (Platform.OS === 'ios' ? error?.code !== 200 : error) {
            console.warn('[DEBUG] email error >>', error);

            // Handle 409 Conflict: Email already exists (excluding if bonus cash is available)
            // If this was a sign-up attempt, automatically retry as sign-in
            if (error?.code === 409 && isSignUp && !isBonusAvailable) {
              console.warn('[DEBUG] Email already exists (409), auto-switching to sign-in flow');

              // Retry with isSignUp: false
              const signInParams = {
                email: email,
                sign_up: false,
                display_name: displayName,
              };

              NetClient.postURL(
                url,
                signInParams,
                SKZAPIManager.OnError.failSilently,
                (retryError: ErrorResponse | null, retryResponse?: unknown) => {
                  try {
                    if (Platform.OS === 'ios' ? retryError?.code !== 200 : retryError) {
                      const retryErrorMessage = Array.isArray(retryError?.responseObject)
                        ? retryError.responseObject[0]?.message
                        : retryError?.responseObject?.message;
                      resolve({
                        success: false,
                        message: retryErrorMessage || 'Failed to send email OTP',
                        code: retryError?.code || undefined,
                        switchedToSignIn: true,
                      });
                    } else {
                      console.warn('[DEBUG] email response after 409 retry--', retryResponse);
                      resolve({
                        success: true,
                        data: {},
                        code: 200,
                        switchedToSignIn: true,
                      });
                    }
                  } catch (err) {
                    console.warn('[DEBUG] email retry fail error--->', err);
                    resolve({
                      success: false,
                      message: 'An unexpected error occurred during retry',
                      switchedToSignIn: true,
                    });
                  }
                }
              );
              return;
            }

            // For other errors, return as usual
            const errorMessage = Array.isArray(error?.responseObject)
              ? error.responseObject[0]?.message
              : error?.responseObject?.message;
            resolve({
              success: false,
              message: errorMessage || 'Failed to send email OTP',
              code: error?.code || undefined,
            });
          } else {
            resolve({success: true, data: {}, code: 200});
          }
        } catch (error) {
          resolve({
            success: false,
            message: 'An unexpected error occurred',
          });
        }
      }
    );
  });
};

/**
 * Verifies an email OTP code
 * @param params - Configuration object containing email, token, and isSignUp flag
 * @returns Promise<EmailOTPResponse> - Response indicating success/failure and optional error message
 */
export const verifyEmailOTP = async (
  params: VerifyEmailOTPParams,
  responseCallback: (data: any, success: boolean) => void
) => {
  const gameId = SkillzModule.App.GameId;

  const requestParams = {
    email: params.email,
    token: params.token,
    provide_email_verification_bonus: params.provide_email_verification_bonus,
  };

  try {
    const {user_id} = getUserDetails();

    if (params.isSignUp) {
      try {
        const url = APIUtilities.generateURL('/user/v1/email-verification-request/confirm');

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'x-skillz-game': gameId,
            'skillz-user-id': user_id,
            'content-type': 'application/json',
          },
          body: JSON.stringify(requestParams),
        });

        if (response.ok) {
          // Handle all 2xx responses
          const text = await response.text();
          if (text && text.length > 0) {
            try {
              const jsonResponse = JSON.parse(text);
              responseCallback(jsonResponse, true);
              console.warn('[DEBUG] Email verification response:', jsonResponse);
            } catch (parseError) {
              responseCallback({}, true);
              console.warn('[DEBUG] Email verification successful but response not JSON');
            }
          } else {
            responseCallback({}, true);
            console.warn(`[DEBUG] Email verification successful (${response.status})`);
          }
        } else {
          const errorResponse = await response.json();
          responseCallback(errorResponse, false);
          console.warn('Email verification failing.', errorResponse);
        }
      } catch (error) {
        responseCallback(null, false);
        console.error('[DEBUG] OAuth token request failed:', error);
      }
    } else {
      const baseURL = APIUtilities._getServerURL();
      const fullURL = `${baseURL}/oauth/email`;

      try {
        const response = await fetch(fullURL, {
          method: 'POST',
          headers: {
            'x-skillz-game': gameId,
            'skillz-user-id': user_id,
            'content-type': 'application/json',
          },
          body: JSON.stringify(requestParams),
        });

        if (response.ok) {
          // Handle all 2xx responses
          const text = await response.text();
          if (text && text.length > 0) {
            try {
              const jsonResponse = JSON.parse(text);
              console.warn('[DEBUG] OAuth token response:', jsonResponse);
              responseCallback(jsonResponse, true);
            } catch (parseError) {
              responseCallback({}, true);
              console.warn('[DEBUG] OAuth token successful but response not JSON');
            }
          } else {
            responseCallback({}, true);
            console.warn(`[DEBUG] OAuth token successful (${response.status})`);
          }
        } else {
          const errorResponse = await response.json();
          console.error('[DEBUG] OAuth token error:', errorResponse);
          responseCallback(errorResponse, false);
        }
      } catch (error) {
        console.error('[DEBUG] OAuth token request failed:', error);
        responseCallback(null, false);
      }
    }
  } catch (error) {
    console.error('OTP verification failed:', error);
    responseCallback(null, false);
  }
};

// Export default object for backward compatibility
const EmailOTPUtils = {
  sendEmailOTP,
  verifyEmailOTP,
};

export default EmailOTPUtils;
